import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';
import { useCharacterStore } from './characterStore';
import { isCurrentUserGM } from '../hooks/useIsGM';

/**
 * Encounter Store — CANONICAL source of truth for combat state.
 *
 * Holds the live encounter (participants, turn order, round). DB-backed via
 * machina_combat_state (participants jsonb column).
 *
 * initiativeStore + combatStore are now derived VIEWS over this store. They
 * subscribe to changes here and expose read-only snapshots; any write they
 * receive forwards back here. There is one writer; there is no drift.
 */

// =============================================================================
// TYPES
// =============================================================================

export interface Participant {
  id: string;           // character UUID or mob UUID
  type: 'character' | 'mob';
  name: string;
  initiative: number;
  dexMod: number;
  isAlive: boolean;
  hasActed: boolean;
}

export interface Encounter {
  id: string;           // DB row id (machina_combat_state.id)
  sceneId: string;
  campaignId: string;
  status: 'setup' | 'active' | 'completed';
  participants: Map<string, Participant>;
  currentTurnId: string | null;
  round: number;
  mapId: string | null;
}

interface DbCombatState {
  id: string;
  scene_id: string;
  round_number: number;
  current_turn_index: number;
  is_active: boolean;
  initiative_order: Array<{ characterId: string; initiative: number; dexMod?: number }>;
  participants: SerializedParticipant[];
}

interface SerializedParticipant {
  id: string;
  type: 'character' | 'mob';
  name: string;
  initiative: number;
  dexMod: number;
  isAlive: boolean;
  hasActed: boolean;
}

// =============================================================================
// STATE
// =============================================================================

interface EncounterState {
  activeEncounter: Encounter | null;

  // Supabase sync
  realtimeCleanup: (() => void) | null;
  _insertInFlight: boolean;   // guards against duplicate INSERTs
  _needsResync: boolean;      // queues a re-sync after INSERT completes
  _lastLocalMutationAt: number; // timestamp of last local participant change — used to skip stale realtime echoes

  // Actions — lifecycle
  startEncounter: (sceneId: string, campaignId: string) => void;
  addParticipant: (id: string, type: 'character' | 'mob', name: string, initiative: number, dexMod: number) => void;
  removeParticipant: (id: string) => void;
  beginCombat: () => void;
  nextTurn: () => void;
  prevTurn: () => void;
  markDead: (id: string) => void;
  markAlive: (id: string) => void;
  endEncounter: () => Promise<void>;

  // Actions — updates
  setParticipantInitiative: (id: string, initiative: number) => void;
  updateParticipantName: (id: string, name: string) => void;

  // Queries
  getParticipantsSorted: () => Participant[];
  getCurrentParticipant: () => Participant | null;
  isInCombat: () => boolean;

  // Supabase sync
  loadFromSupabase: (sceneId: string, campaignId: string) => Promise<void>;
  syncToSupabase: () => Promise<void>;
  subscribeToRealtime: (sceneId: string) => void;
  unsubscribeFromRealtime: () => void;
}

// =============================================================================
// HELPERS
// =============================================================================

function sortParticipants(participants: Participant[]): Participant[] {
  return [...participants].sort((a, b) => {
    if (b.initiative !== a.initiative) return b.initiative - a.initiative;
    return b.dexMod - a.dexMod;
  });
}

// pushToInitiativeStore removed — initiativeStore now subscribes to this
// store and derives its entries automatically on every mutation. No manual
// push needed, and the previous recursion risk (init.add → enc.add →
// pushToInit → init.add again) is gone.
function pushToInitiativeStore(_encounter: Encounter | null): void {
  // No-op — kept as a symbol so existing callers don't break during the
  // migration window. Safe to delete once all call sites have been cleaned up.
}

function serializeParticipants(encounter: Encounter): SerializedParticipant[] {
  return Array.from(encounter.participants.values());
}

function deserializeParticipants(arr: SerializedParticipant[]): Map<string, Participant> {
  const map = new Map<string, Participant>();
  for (const p of arr) {
    map.set(p.id, p);
  }
  return map;
}

// Drop character-type participants whose UUIDs don't resolve in the current
// campaign's character roster — these are cross-campaign bleed left in the
// JSONB blob (e.g. a solo-campaign Mr. Finch sticking around in a Tomb scene).
// Only scrubs when characterStore is loaded for the same campaignId, so a
// mid-switch reload doesn't wrongly purge valid entries.
function scrubOrphans(
  participants: Map<string, Participant>,
  campaignId: string,
): { cleaned: Map<string, Participant>; dropped: number } {
  const charStore = useCharacterStore.getState();
  if (charStore.currentCampaignId !== campaignId || charStore.characters.size === 0) {
    return { cleaned: participants, dropped: 0 };
  }
  const cleaned = new Map<string, Participant>();
  let dropped = 0;
  for (const [id, p] of participants) {
    if (p.type === 'character' && !charStore.characters.has(id)) {
      console.warn(`[EncounterStore] Scrubbing orphan participant: ${p.name} (${id}) — not in campaign ${campaignId}`);
      dropped++;
      continue;
    }
    cleaned.set(id, p);
  }
  return { cleaned, dropped };
}

// Also produce the legacy initiative_order array for backward compat
function toLegacyInitiativeOrder(encounter: Encounter): Array<{ characterId: string; initiative: number; dexMod: number }> {
  return Array.from(encounter.participants.values()).map(p => ({
    characterId: p.id,
    initiative: p.initiative,
    dexMod: p.dexMod,
  }));
}

// =============================================================================
// STORE
// =============================================================================

export const useEncounterStore = create<EncounterState>((set, get) => ({
  activeEncounter: null,
  realtimeCleanup: null,
  _insertInFlight: false,
  _needsResync: false,
  _lastLocalMutationAt: 0,

  // ---------------------------------------------------------------------------
  // LIFECYCLE
  // ---------------------------------------------------------------------------

  startEncounter: (sceneId, campaignId) => {
    const encounter: Encounter = {
      id: '', // Will be set on first DB sync
      sceneId,
      campaignId,
      status: 'setup',
      participants: new Map(),
      currentTurnId: null,
      round: 0,
      mapId: null,
    };
    set({ activeEncounter: encounter });
  },

  addParticipant: (id, type, name, initiative, dexMod) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const participant: Participant = {
      id,
      type,
      name,
      initiative,
      dexMod,
      isAlive: true,
      hasActed: false,
    };

    const newParticipants = new Map(activeEncounter.participants);
    newParticipants.set(id, participant);

    set({
      activeEncounter: { ...activeEncounter, participants: newParticipants },
      _lastLocalMutationAt: Date.now(),
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  removeParticipant: (id) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const newParticipants = new Map(activeEncounter.participants);
    newParticipants.delete(id);

    // If we removed the current turn holder, advance
    let currentTurnId = activeEncounter.currentTurnId;
    if (currentTurnId === id) {
      const sorted = sortParticipants(Array.from(newParticipants.values()).filter(p => p.isAlive));
      currentTurnId = sorted.length > 0 ? sorted[0].id : null;
    }

    set({
      activeEncounter: { ...activeEncounter, participants: newParticipants, currentTurnId },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  beginCombat: () => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const sorted = sortParticipants(Array.from(activeEncounter.participants.values()).filter(p => p.isAlive));
    if (sorted.length === 0) return;

    // Reset hasActed
    const newParticipants = new Map(activeEncounter.participants);
    for (const [id, p] of newParticipants) {
      newParticipants.set(id, { ...p, hasActed: false });
    }

    set({
      activeEncounter: {
        ...activeEncounter,
        status: 'active',
        participants: newParticipants,
        round: 1,
        currentTurnId: sorted[0].id,
      },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  nextTurn: () => {
    const { activeEncounter } = get();
    if (!activeEncounter || activeEncounter.status !== 'active') return;

    const sorted = sortParticipants(
      Array.from(activeEncounter.participants.values()).filter(p => p.isAlive)
    );
    if (sorted.length === 0) return;

    const currentIdx = sorted.findIndex(p => p.id === activeEncounter.currentTurnId);
    const newParticipants = new Map(activeEncounter.participants);

    // Mark current as acted
    if (activeEncounter.currentTurnId) {
      const current = newParticipants.get(activeEncounter.currentTurnId);
      if (current) {
        newParticipants.set(current.id, { ...current, hasActed: true });
      }
    }

    let nextIdx = currentIdx + 1;
    let newRound = activeEncounter.round;

    if (nextIdx >= sorted.length) {
      // New round — reset hasActed
      nextIdx = 0;
      newRound++;
      for (const [id, p] of newParticipants) {
        newParticipants.set(id, { ...p, hasActed: false });
      }
    }

    set({
      activeEncounter: {
        ...activeEncounter,
        participants: newParticipants,
        currentTurnId: sorted[nextIdx].id,
        round: newRound,
      },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  prevTurn: () => {
    const { activeEncounter } = get();
    if (!activeEncounter || activeEncounter.status !== 'active') return;

    const sorted = sortParticipants(
      Array.from(activeEncounter.participants.values()).filter(p => p.isAlive)
    );
    if (sorted.length === 0) return;

    const currentIdx = sorted.findIndex(p => p.id === activeEncounter.currentTurnId);

    let prevIdx = currentIdx - 1;
    let newRound = activeEncounter.round;

    if (prevIdx < 0) {
      prevIdx = sorted.length - 1;
      newRound = Math.max(1, newRound - 1);
    }

    const newParticipants = new Map(activeEncounter.participants);
    // Un-act the previous participant
    const prev = newParticipants.get(sorted[prevIdx].id);
    if (prev) {
      newParticipants.set(prev.id, { ...prev, hasActed: false });
    }

    set({
      activeEncounter: {
        ...activeEncounter,
        participants: newParticipants,
        currentTurnId: sorted[prevIdx].id,
        round: newRound,
      },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  markDead: (id) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const newParticipants = new Map(activeEncounter.participants);
    const p = newParticipants.get(id);
    if (!p) return;

    newParticipants.set(id, { ...p, isAlive: false });

    // If dead participant was current turn, advance to next alive in initiative order
    let currentTurnId = activeEncounter.currentTurnId;
    if (currentTurnId === id) {
      const allSorted = sortParticipants(Array.from(newParticipants.values()));
      const deadIdx = allSorted.findIndex(pp => pp.id === id);
      // Walk forward from dead participant's position, wrapping around, to find next alive
      let nextAlive: string | null = null;
      for (let offset = 1; offset < allSorted.length; offset++) {
        const candidate = allSorted[(deadIdx + offset) % allSorted.length];
        if (candidate.isAlive) {
          nextAlive = candidate.id;
          break;
        }
      }
      currentTurnId = nextAlive;
    }

    set({
      activeEncounter: {
        ...activeEncounter,
        participants: newParticipants,
        currentTurnId,
      },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  markAlive: (id) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const newParticipants = new Map(activeEncounter.participants);
    const p = newParticipants.get(id);
    if (!p) return;

    newParticipants.set(id, { ...p, isAlive: true });

    set({
      activeEncounter: { ...activeEncounter, participants: newParticipants },
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  endEncounter: async () => {
    const { activeEncounter } = get();
    // initiativeStore mirrors automatically via subscription — no manual clear needed

    if (activeEncounter) {
      // Sync completed state to DB, then clear
      set({
        activeEncounter: { ...activeEncounter, status: 'completed' },
      });
      await get().syncToSupabase();
    }
    set({ activeEncounter: null });
  },

  // ---------------------------------------------------------------------------
  // UPDATES
  // ---------------------------------------------------------------------------

  setParticipantInitiative: (id, initiative) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const newParticipants = new Map(activeEncounter.participants);
    const p = newParticipants.get(id);
    if (!p) return;

    newParticipants.set(id, { ...p, initiative });

    set({
      activeEncounter: { ...activeEncounter, participants: newParticipants },
      _lastLocalMutationAt: Date.now(),
    });

    pushToInitiativeStore(get().activeEncounter);
    get().syncToSupabase();
  },

  updateParticipantName: (id, name) => {
    const { activeEncounter } = get();
    if (!activeEncounter) return;

    const newParticipants = new Map(activeEncounter.participants);
    const p = newParticipants.get(id);
    if (!p) return;

    newParticipants.set(id, { ...p, name });

    set({
      activeEncounter: { ...activeEncounter, participants: newParticipants },
    });

    pushToInitiativeStore(get().activeEncounter);
  },

  // ---------------------------------------------------------------------------
  // QUERIES
  // ---------------------------------------------------------------------------

  getParticipantsSorted: () => {
    const { activeEncounter } = get();
    if (!activeEncounter) return [];
    return sortParticipants(Array.from(activeEncounter.participants.values()));
  },

  getCurrentParticipant: () => {
    const { activeEncounter } = get();
    if (!activeEncounter?.currentTurnId) return null;
    return activeEncounter.participants.get(activeEncounter.currentTurnId) ?? null;
  },

  isInCombat: () => {
    const { activeEncounter } = get();
    return activeEncounter?.status === 'active';
  },

  // ---------------------------------------------------------------------------
  // SUPABASE SYNC
  // ---------------------------------------------------------------------------

  loadFromSupabase: async (sceneId, campaignId) => {
    try {
      const { data, error } = await supabase
        .from('machina_combat_state')
        .select('*')
        .eq('scene_id', sceneId)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      if (data) {
        const dbState = data as unknown as DbCombatState;
        const participantArr = (dbState.participants ?? []) as SerializedParticipant[];
        const rawParticipants = deserializeParticipants(participantArr);
        const { cleaned: participants, dropped } = scrubOrphans(rawParticipants, campaignId);

        // Truly empty + inactive = stale ghost row from a prior battle — skip.
        // A row with participants but is_active=false is a valid setup-mode
        // encounter (spawns added pre-Start-Combat); hydrate it so the init
        // list survives a refresh mid-prep.
        if (!dbState.is_active && participants.size === 0) {
          console.log('[EncounterStore] Skipping empty inactive encounter (stale combat_state)');
          set({ activeEncounter: null });
          return;
        }

        const sorted = sortParticipants(Array.from(participants.values()));
        const turnIdx = Math.min(dbState.current_turn_index, sorted.length - 1);
        const currentTurnId = sorted.length > 0 && turnIdx >= 0 ? sorted[turnIdx].id : null;

        const encounter: Encounter = {
          id: dbState.id,
          sceneId,
          campaignId,
          status: dbState.is_active ? 'active' : 'setup',
          participants,
          currentTurnId,
          round: dbState.round_number,
          mapId: null,
        };

        set({ activeEncounter: encounter });
        pushToInitiativeStore(encounter);

        // Do NOT write the scrubbed view back. With multiple clients connected,
        // each one's characterStore visibility is different (players often see
        // only their own PC, not the full party), so each load-scrub would
        // write a DIFFERENT subset back, with subsequent clients overwriting
        // earlier ones — destroying GM-authored state. Local view is scrubbed
        // for display; DB is treated as canonical and preserved as-is.
        if (dropped > 0) {
          console.warn(
            `[EncounterStore] Load-scrub dropped ${dropped} char participant(s). NOT writing back — local view scrubbed for display only.`
          );
        }
      }
    } catch (err) {
      console.error('[EncounterStore] Failed to load:', err);
    }
  },

  syncToSupabase: async () => {
    // PUB/SUB GATE: combat_state is GM-owned. Players are pure subscribers.
    // Without this, any non-GM client mutation cascades into a multi-client
    // feedback loop (different characterStore visibility per client → each
    // writes a different scrubbed view → overwrites GM-authored state).
    if (!isCurrentUserGM()) {
      return;
    }

    const { activeEncounter } = get();
    if (!activeEncounter?.sceneId) return;

    // Guard: if an INSERT is already in flight, queue a re-sync instead of
    // firing a second INSERT (which would create a duplicate row).
    if (!activeEncounter.id && get()._insertInFlight) {
      set({ _needsResync: true });
      return;
    }

    try {
      const sorted = sortParticipants(Array.from(activeEncounter.participants.values()));
      const currentTurnIndex = activeEncounter.currentTurnId
        ? sorted.findIndex(p => p.id === activeEncounter.currentTurnId)
        : 0;

      const payload = {
        scene_id: activeEncounter.sceneId,
        is_active: activeEncounter.status === 'active',
        initiative_order: toLegacyInitiativeOrder(activeEncounter),
        current_turn_index: Math.max(0, currentTurnIndex),
        round_number: activeEncounter.round,
        participants: serializeParticipants(activeEncounter),
        updated_at: new Date().toISOString(),
      };

      if (activeEncounter.id) {
        const { error } = await supabase
          .from('machina_combat_state')
          .update(payload)
          .eq('id', activeEncounter.id);
        if (error) throw error;
      } else {
        set({ _insertInFlight: true });
        try {
          const { data, error } = await supabase
            .from('machina_combat_state')
            .insert(payload)
            .select('id')
            .single();
          if (error) throw error;
          if (data) {
            set({
              activeEncounter: { ...get().activeEncounter!, id: data.id },
            });
          }
        } finally {
          set({ _insertInFlight: false });
        }

        // Flush any mutations that arrived while the INSERT was in flight
        if (get()._needsResync) {
          set({ _needsResync: false });
          get().syncToSupabase();
        }
      }
    } catch (err) {
      console.error('[EncounterStore] Failed to sync:', err);
    }
  },

  subscribeToRealtime: (sceneId) => {
    get().unsubscribeFromRealtime();

    const { cleanup } = createSubscription({
      channelName: `encounter:${sceneId}`,
      table: 'machina_combat_state',
      filter: `scene_id=eq.${sceneId}`,
      event: '*',
      onPayload: (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          const dbState = payload.new as unknown as DbCombatState;

          // Skip stale realtime echoes that arrive shortly after a local mutation.
          // Without this, the INSERT/UPDATE echo from our own write can overwrite
          // participants added locally between the write and the echo arrival.
          const msSinceMutation = Date.now() - get()._lastLocalMutationAt;
          if (msSinceMutation < 2000) {
            return;
          }

          const participantArr = (dbState.participants ?? []) as SerializedParticipant[];
          const rawParticipants = deserializeParticipants(participantArr);
          const enc = get().activeEncounter;
          const campaignIdForScrub = enc?.campaignId ?? '';
          const { cleaned: participants, dropped } = scrubOrphans(rawParticipants, campaignIdForScrub);

          // Empty + inactive = truly ended encounter → wipe. A setup encounter
          // with participants but is_active=false (because combat hasn't started
          // yet) still owns valid state; preserve it. Without this split,
          // every spawn triggered a self-erasure via its own realtime echo
          // since sync writes is_active = (status === 'active').
          if (!dbState.is_active && participants.size === 0) {
            set({ activeEncounter: null });
            return;
          }

          const sorted = sortParticipants(Array.from(participants.values()));
          const turnIdx = Math.min(dbState.current_turn_index, sorted.length - 1);
          const currentTurnId = sorted.length > 0 && turnIdx >= 0 ? sorted[turnIdx].id : null;

          const encounter: Encounter = {
            id: dbState.id,
            sceneId,
            campaignId: campaignIdForScrub,
            status: dbState.is_active ? 'active' : 'setup',
            participants,
            currentTurnId,
            round: dbState.round_number,
            mapId: enc?.mapId ?? null,
          };

          set({ activeEncounter: encounter });
          pushToInitiativeStore(encounter);
          // NEVER sync back from a realtime echo. Each connected client has
          // its own characterStore visibility (players often only see their
          // own PC, not the full party) — if every client wrote its scrubbed
          // view of participants back to DB, every player joining would
          // overwrite the GM-authored state and cascade into an N-client
          // feedback loop. Scrubbing on load is fine (it self-heals stale
          // rows once); scrubbing on echo + writing back is the bug.
          if (dropped > 0) {
            console.warn(
              `[EncounterStore] Realtime echo dropped ${dropped} char participant(s) — local view scrubbed, but NOT writing back. Other clients may legitimately see chars this client cannot.`
            );
          }
        } else if (payload.eventType === 'DELETE') {
          // initiativeStore mirrors automatically via subscription.
          set({ activeEncounter: null });
        }
      },
    });

    set({ realtimeCleanup: cleanup });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },
}));
